import * as stylex from '@stylexjs/stylex';

/**
 * KEYFRAMES
 */
const rotate = stylex.keyframes({
    '0%': { transform: 'rotate(0deg)' },
    '100%': { transform: 'rotate(360deg)' },
});

// Unused pulse keyframe removed

const float1 = stylex.keyframes({
    '0%, 100%': { transform: 'translateY(0px) rotate(0deg)' },
    '50%': { transform: 'translateY(-20px) rotate(5deg)' },
});

const float2 = stylex.keyframes({
    '0%, 100%': { transform: 'translateY(0px) rotate(25deg)' },
    '50%': { transform: 'translateY(-15px) rotate(30deg)' },
});

const float3 = stylex.keyframes({
    '0%, 100%': { transform: 'translateY(0px) rotate(-8deg)' },
    '50%': { transform: 'translateY(-25px) rotate(-3deg)' },
});

const float4 = stylex.keyframes({
    '0%, 100%': { transform: 'translateY(0px) rotate(-12deg)' },
    '50%': { transform: 'translateY(-18px) rotate(-17deg)' },
});

/**
 * BREAKPOINTS
 */
const MEDIA_1024 = '@media (max-width: 1024px)';
const MEDIA_768 = '@media (max-width: 768px)';
const MEDIA_360 = '@media (max-width: 360px)';

export const styles = stylex.create({
    /* Page Layout */
    modelPage: {
        maxWidth: '1200px',
        marginInline: 'auto',
        padding: {
            default: 'var(--spacing-lg)',
            [MEDIA_1024]: 'var(--spacing-md)',
            [MEDIA_360]: 'var(--spacing-sm)',
        },
        color: 'var(--text-primary)',
    },
    backLink: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: 'var(--spacing-sm)',
        color: { default: 'var(--text-secondary)', ':hover': 'var(--primary)' },
        textDecoration: 'none',
        fontSize: 'var(--font-size-sm)',
        marginBottom: 'var(--spacing-md)',
        transitionProperty: 'color',
        transitionDuration: 'var(--transition-fast)',
    },

    /* Hero Section */
    heroSection: {
        position: 'relative',
        minHeight: { default: '600px', [MEDIA_1024]: '650px', [MEDIA_360]: '400px' },
        borderRadius: 'var(--radius-xl)',
        marginBottom: 'var(--spacing-3xl)',
        overflow: 'hidden',
        display: 'flex',
        alignItems: 'center',
    },
    speechHeroAnimated: {
        backgroundImage: `linear-gradient(135deg, var(--bg-card) 0%, rgba(59, 130, 246, 0.02) 25%, rgba(16, 185, 129, 0.02) 75%, var(--bg-card) 100%)`,
        borderWidth: '1px',
        borderStyle: 'solid',
        borderColor: 'var(--border)',
        '::before': {
            content: '""',
            position: 'absolute',
            inset: 0,
            backgroundImage: `radial-gradient(circle at 20% 50%, rgba(59, 130, 246, 0.03) 0%, transparent 50%), radial-gradient(circle at 80% 50%, rgba(16, 185, 129, 0.03) 0%, transparent 50%)`,
        },
        '::after': {
            content: '""',
            position: 'absolute',
            inset: '-50%',
            backgroundImage: `conic-gradient(from 0deg at 50% 50%, transparent 0deg, rgba(59, 130, 246, 0.015) 45deg, transparent 90deg)`,
            animationName: rotate,
            animationDuration: '120s',
            animationTimingFunction: 'linear',
            animationIterationCount: 'infinite',
            opacity: 0.3,
        }
    },
    heroContent: {
        position: 'relative',
        zIndex: 2,
        padding: { default: 'var(--spacing-3xl)', [MEDIA_768]: 'var(--spacing-lg)', [MEDIA_360]: 'var(--spacing-md)' },
        maxWidth: { default: '600px', [MEDIA_768]: '100%' },
    },
    heroTitle: {
        fontSize: { default: '3rem', [MEDIA_768]: '2.2rem', [MEDIA_360]: '1.8rem' },
        fontWeight: 800,
        marginBlock: '0 var(--spacing-md)',
        textShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
    },
    heroDescription: {
        fontSize: 'var(--font-size-lg)',
        color: 'var(--text-secondary)',
        lineHeight: 1.6,
        marginBottom: 'var(--spacing-xl)',
    },
    heroStats: {
        display: 'flex',
        gap: 'var(--spacing-lg)',
        flexWrap: 'wrap',
        flexDirection: { [MEDIA_768]: 'column' },
    },
    stat: {
        display: 'flex',
        alignItems: 'center',
        gap: 'var(--spacing-sm)',
        color: 'var(--text-secondary)',
        fontWeight: 500,
    },
    statSvg: { color: 'var(--accent)' },
    creditCostBadge: {
        backgroundColor: 'var(--accent)',
        color: 'var(--text-on-accent)',
        paddingBlock: 'var(--spacing-sm)',
        paddingInline: 'var(--spacing-md)',
        borderRadius: 'var(--radius-full)',
        fontSize: 'var(--font-size-sm)',
        fontWeight: 600,
        marginTop: 'var(--spacing-md)',
        display: 'inline-block',
    },

    /* Voice/Audio Players */
    heroVoicePlayer: {
        marginTop: 'var(--spacing-md)',
        display: 'flex',
        alignItems: 'center',
        gap: 'var(--spacing-md)',
        color: 'hsla(var(--hue-primary), 85%, 60%, .4)'
    },
    heroAudioPlayer: {
        width: '100%',
        outline: 'none',
        borderRadius: 'var(--radius-md)',
        '::-webkit-media-controls-panel': {
            backgroundColor: '#9ca5afff',
            color: 'white',
        }
    },
    audioLabel: { fontSize: 'var(--font-size-sm)', fontWeight: 600, textTransform: 'uppercase', whiteSpace: 'nowrap' },

    /* Note Section */
    importantNote: {
        backgroundImage: 'linear-gradient(135deg, rgba(59, 130, 246, 0.1), rgba(147, 51, 234, 0.1))',
        borderWidth: '1px',
        borderStyle: 'solid',
        borderColor: 'rgba(59, 130, 246, 0.2)',
        borderRadius: 'var(--radius-lg)',
        padding: 'var(--spacing-xl)',
        marginBottom: 'var(--spacing-xl)',
        display: 'flex',
        gap: 'var(--spacing-lg)',
        alignItems: 'flex-start',
        flexDirection: { [MEDIA_768]: 'column' },
    },
    inlineChatButton: {
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '48px',
        height: '48px',
        backgroundColor: 'var(--bg-secondary)',
        borderWidth: '1px',
        borderStyle: 'solid',
        borderColor: 'var(--border)',
        borderRadius: 'var(--radius-lg)',
        color: 'var(--accent-light)',
    },
    noteIcon: { flexShrink: 0 },
    noteContent: { flex: 1 },

    /* Gallery & Search */
    gallerySection: { marginBottom: 'var(--spacing-3xl)' },
    sectionTitle: {
        fontSize: '2rem',
        fontWeight: 700,
        marginBlock: '0 var(--spacing-xl)',
        position: 'relative',
        '::after': {
            content: '""',
            position: 'absolute',
            bottom: '-8px',
            left: 0,
            width: '60px',
            height: '3px',
            backgroundImage: 'linear-gradient(90deg, var(--primary), var(--accent))',
            borderRadius: 'var(--radius-full)',
        }
    },
    galleryDescription: {
        fontSize: 'var(--font-size-sm)',
        color: 'var(--text-secondary)',
        lineHeight: 1.6,
        marginBottom: 'var(--spacing-xl)',
        textAlign: 'left',
        whiteSpace: 'normal',
    },
    searchContainer: { marginBottom: 'var(--spacing-xl)' },
    searchInputWrapper: { position: 'relative', maxWidth: '600px', marginInline: 'auto' },
    searchIcon: { position: 'absolute', left: 'var(--spacing-md)', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-secondary)' },
    searchInput: {
        width: '100%',
        paddingTop: 'var(--spacing-md)',
        paddingBottom: 'var(--spacing-md)',
        paddingLeft: 'calc(var(--spacing-md) * 2 + 20px)',
        paddingRight: 'var(--spacing-md)',
        borderWidth: '2px',
        borderStyle: 'solid',
        borderColor: { default: 'var(--border)', ':focus': 'var(--primary)' },
        borderRadius: 'var(--radius-full)',
        backgroundColor: 'var(--bg-primary)',
        color: 'var(--text-primary)',
        outline: 'none',
    },
    searchResults: { textAlign: 'center', marginTop: 'var(--spacing-sm)', color: 'var(--text-secondary)', fontSize: 'var(--font-size-sm)' },

    /* Category/Grid Items */
    categoriesContainer: { display: 'flex', flexDirection: 'column', gap: 'var(--spacing-md)' },
    categoryItem: { borderWidth: '1px', borderStyle: 'solid', borderColor: 'var(--border)', borderRadius: 'var(--radius-lg)', overflow: 'hidden' },
    categoryHeader: { backgroundColor: { default: 'var(--bg-card)', ':hover': 'var(--bg-tertiary)' }, padding: 'var(--spacing-md) var(--spacing-lg)', cursor: 'pointer' },
    categoryName: { display: 'flex', alignItems: 'center', gap: 'var(--spacing-sm)', fontWeight: 600, fontSize: 'var(--font-size-lg)' },
    itemCount: { color: 'var(--text-secondary)', fontWeight: 400, fontSize: 'var(--font-size-sm)' },
    categoryContent: { padding: 'var(--spacing-lg)' },
    soundEffectsList: { display: 'flex', flexDirection: 'column', gap: 'var(--spacing-md)' },

    /* Sound Effect Item (Atomic logic for hover) */
    soundEffectGalleryItem: {
        backgroundColor: 'var(--bg-card)',
        borderWidth: '1px',
        borderStyle: 'solid',
        borderColor: 'var(--border)',
        borderRadius: 'var(--radius-lg)',
        padding: 'var(--spacing-md)',
        display: 'flex',
        alignItems: 'center',
        minHeight: '60px',
        transitionProperty: 'all',
        transitionDuration: 'var(--transition-normal)',
        backgroundImage: 'linear-gradient(135deg, var(--bg-card) 0%, rgba(59, 130, 246, 0.02) 100%)',
        flexDirection: { [MEDIA_768]: 'column' },
    },
    soundEffectItemHovered: {
        borderColor: 'var(--primary)',
        boxShadow: 'var(--shadow-lg)',
        transform: 'translateY(-2px)',
    },
    soundEffectInfoContainer: {
        flex: 1,
        display: 'flex',
        alignItems: 'center',
        gap: 'var(--spacing-sm)',
    },
    soundEffectPrompt: {
        fontSize: 'var(--font-size-md)',
        fontWeight: 600,
        color: 'var(--text-primary)',
    },
    audioPlayerContainer: {
        flexShrink: 0,
        width: { default: '40%', [MEDIA_768]: '100%' },
    },
    audioPlayer: {
        width: '100%',
        height: '32px',
        '::-webkit-media-controls-panel': {
            backgroundColor: '#9ca5afff',
            color: 'white',
        }
    },

    clickIndicatorSoundEffect: {
        padding: 'var(--spacing-xs) var(--spacing-sm)',
        borderRadius: 'var(--radius-full)',
        borderWidth: '1px',
        borderStyle: 'solid',
        borderColor: 'var(--primary)',
        color: 'var(--primary)',
        fontSize: '0.75rem',
        fontWeight: 500,
        opacity: 0,
        transform: 'scale(0.8)',
        transitionProperty: 'all',
        transitionDuration: 'var(--transition-normal)',
        cursor: 'pointer',
        whiteSpace: 'nowrap',
        backdropFilter: 'blur(10px)',
    },
    clickIndicatorVisible: {
        opacity: 1,
        transform: 'scale(1)',
    },

    /* Floating Icons */
    floatingIconsContainer: {
        position: 'absolute',
        top: 0,
        right: 0,
        width: '50%',
        height: '100%',
        pointerEvents: 'none',
        zIndex: 1,
    },
    floatingIcon: {
        position: 'absolute',
        color: 'rgba(255, 255, 255, 0.15)',
        opacity: 0.8,
        transitionProperty: 'all',
        transitionDuration: '0.3s',
        transitionTimingFunction: 'ease',
    },
    // StyleX doesn't support ".parent svg" selectors, 
    // so apply this style directly to the Lucide icon component
    floatingIconSvg: {
        filter: 'drop-shadow(0 2px 8px rgba(0, 0, 0, 0.1))',
    },
    floatingIcon1: {
        top: '15%',
        right: '20%',
        animationName: float1,
        animationDuration: '8s',
        animationTimingFunction: 'ease-in-out',
        animationIterationCount: 'infinite',
        color: 'rgba(59, 130, 246, 0.2)',
    },
    floatingIcon2: {
        top: '35%',
        right: '10%',
        transform: 'rotate(25deg)',
        animationName: float2,
        animationDuration: '10s',
        animationTimingFunction: 'ease-in-out',
        animationIterationCount: 'infinite',
        color: 'rgba(16, 185, 129, 0.2)',
    },
    floatingIcon3: {
        top: '45%',
        right: '25%',
        transform: 'rotate(-8deg)',
        animationName: float3,
        animationDuration: '120s',
        animationTimingFunction: 'ease-in-out',
        animationIterationCount: 'infinite',
        color: 'rgba(139, 92, 246, 0.2)',
    },
    floatingIcon4: {
        top: '60%',
        right: '15%',
        transform: 'rotate(-12deg)',
        animationName: float4,
        animationDuration: '9s',
        animationTimingFunction: 'ease-in-out',
        animationIterationCount: 'infinite',
        color: 'rgba(245, 101, 101, 0.2)',
    },

});